import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Truncate text to a specified length and add ellipses.
 * @param text - The text to truncate.
 * @param maxLength - The maximum length of the text.
 * @returns The truncated text with ellipses.
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + "...";
}

/**
 * Checks if the given string is a valid email address.
 * @param email - The email string to validate.
 * @returns True if valid, false otherwise.
 */
export function isValidEmail(email: string): boolean {
  // Simple RFC 5322 compliant regex for most emails
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Checks if the given string is a valid 10-digit mobile number (numeric only).
 * @param mobile - The mobile string to validate.
 * @returns True if valid, false otherwise.
 */
export function isValidMobile(mobile: string): boolean {
  return /^\d{10}$/.test(mobile);
}

/**
 * Checks if the given name contains only alphanumeric characters (no special characters).
 * Allows spaces between words, but not leading/trailing spaces.
 * @param name - The name string to validate.
 * @returns True if valid, false otherwise.
 */
export function isValidName(name: string): boolean {
  // Allow alphanumeric, spaces, and single dots.
  // No leading/trailing spaces or dots. No consecutive spaces or dots.
  return /^[A-Za-z0-9]+(?:[. ]+[A-Za-z0-9]+)*$/.test(name);
}

/**
 * Format a date to a standard format.
 * @param date - The date to format.
 * @param withTime - Whether to include the time in the format.
 * @returns The formatted date string.
 */
export function formatDate(date: Date, withTime: boolean = false): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  if (withTime) {
    options.hour = "2-digit";
    options.minute = "2-digit";
    options.hour12 = true;
  }

  return date.toLocaleDateString(undefined, options);
}
